import { Save, UserPlus } from '@tamagui/lucide-icons';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { SheetProps } from '@unistocks/components/sheet/sheet';
import { toast } from '@unistocks/components/toast';
import { IdentityRoleCreateDto } from '@unistocks/proxy/axios-client';
import { useRoleCreateMutation } from '@unistocks/proxy/axios-client/RoleQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { showErrorToast } from '@unistocks/shared/utils/toast';
import React, { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

type CreateRoleSheetProps = Omit<SheetProps, 'children' | 'snapPoints' | 'snapPointsMode'> & {
  onCreated?: () => void;
};

const initialValues: IdentityRoleCreateDto = {
  name: '',
  isDefault: false,
  isPublic: false,
  extraProperties: {}
};

const schemaFactory = () =>
  z.object({
    name: requiredSchema(z.string(), t('AbpIdentity::RoleName')),
    isDefault: z.boolean().default(false),
    isPublic: z.boolean().default(false),
    extraProperties: z.record(z.string(), z.any()).default({})
  });

export const CreateRoleSheet: React.FC<CreateRoleSheetProps> = ({
  open,
  onOpenChange,
  onCreated,
  ...sheetProps
}) => {
  const formRef = useRef<UseFormReturn<IdentityRoleCreateDto>>(null);
  const schema = schemaFactory();

  const { mutateAsync: createRole } = useRoleCreateMutation({
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      onCreated?.();
      onOpenChange?.(false);
    },
    onError: e => {
      showErrorToast(e);
    }
  });

  useEffect(() => {
    if (!open) {
      formRef.current?.reset(initialValues);
    }
  }, [open]);

  const handleSubmit = (values: IdentityRoleCreateDto) =>
    createRole({
      ...values,
      extraProperties: values.extraProperties ?? {}
    });

  return (
    <FullScreenSheet
      open={open}
      onOpenChange={onOpenChange}
      contentTopMargin
      headerTitle={t('AbpIdentity::NewRole')}
      headerIcon={<UserPlus color={'$unistocks'} />}
      {...sheetProps}
    >
      <Form
        ref={formRef}
        schema={schema}
        initialValues={initialValues}
        triggerText={t('AbpUi::Save')}
        triggerIcon={Save}
        onSubmit={handleSubmit}
      >
        <Form.Input
          name="name"
          isRequired
          label={t('AbpIdentity::RoleName')}
          placeholder={t('AbpIdentity::RoleName')}
        />
        <Form.Checkbox name="isDefault" label={t('AbpIdentity::DisplayName:IsDefault')} />
        <Form.Checkbox name="isPublic" label={t('AbpIdentity::DisplayName:IsPublic')} />
      </Form>
    </FullScreenSheet>
  );
};

CreateRoleSheet.displayName = 'CreateRoleSheet';
